<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

import Message from 'primevue/message';
import DSButton from 'primevue/button';
import Tag from 'primevue/tag';

import { useDownloadFile } from '@/composables/useDownloadFile';
import { useUserManagement } from '@/composables/user-management';
import { RouteNames } from '@/data/routes';
import {
  OWNER_ROLES,
  ENTITY_TYPE,
  ID_TYPE,
} from '@clearbanc/data-common-types';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { getCountryName } from '@/utils/address';
import { useUserInviteManagement } from '@/composables/user-invite-management';
import { useUserActionsTooltips } from '@/composables/user-actions-tooltips';
import { userHasRequiredFieldsForVerification } from '@/utils/user-helper';
import { useDocumentUploads } from '@/composables/useDocumentUploads';

// -----
// Setup
// -----

const store = useStore();
const router = useRouter();
const props = defineProps({
  owner: {
    type: Object,
    required: true,
  },
});

const userId = ref(props.owner.id);
const {
  isAuthenticatedUser,
  canEditUser,
  canSetAsSignatory,
  isEligibleToBeSignatory,
  isUserProfileCompleted,
} = useUserManagement(userId);

const { canResendInvite, resendInvite, loadingUserInvite, hasPendingInvite } =
  useUserInviteManagement(userId);
const { trashIconTooltipText, editButtonTooltipText } =
  useUserActionsTooltips(userId);
const { areDocumentUploadsRequired } = useDocumentUploads();

defineEmits(['removeOwner', 'setAsSignatory']);

// ---------
// Variables
// ---------
const isSignatory = computed(() =>
  props.owner.jobRoles.includes(OWNER_ROLES.SIGNATORY),
);
const isCompanyOwner = computed(() =>
  props.owner.jobRoles.includes(OWNER_ROLES.OWNER),
);
const isDirectorBoardMember = computed(() =>
  props.owner.jobRoles.includes(OWNER_ROLES.DIRECTOR),
);
const canEditOwner = computed(() => {
  return canEditUser.value && isAuthenticatedUser.value;
});

// ------------
// Misc Methods
// ------------

function formatAddress(address) {
  if (!address) {
    return '';
  }

  return [
    address.line1 && `${address.line1}, `,
    address.city && `${address.city}<br />`,
    address.state && `${address.state}, `,
    address.postalCode && `${address.postalCode}, `,
    address.country && getCountryName(address.country),
  ]
    .filter((t) => !!t)
    .join('')
    .trim();
}

function ownerHasIdUploads(owner) {
  const fileFilters = {
    metaId: `${owner.id}`,
    entity: owner.isPrimary ? ENTITY_TYPE.USER : ENTITY_TYPE.OWNER,
    type: [
      ID_TYPE.PASSPORT,
      ID_TYPE.DRIVER_LICENSE,
      ID_TYPE.ID,
      ID_TYPE.PASSPORT_OR_LICENSE,
    ],
  };

  return !!store.getters.uploadsMatching(fileFilters).length;
}

// --------------
// Download Files
// --------------

const { downloadFile } = useDownloadFile();

async function downloadFiles(ownerId) {
  const ids = [];

  store.getters.uploads.forEach((file) => {
    if (file.metaId === ownerId.toString()) {
      ids.push(file.id);
    }
  });

  await Promise.all(ids.map((id) => downloadFile(id)));
}

// ----------
// Edit Owner
// ----------

async function editOwner() {
  analytics.track(SEGMENT_EVENTS.if_user_profile_btn_edit, {
    business_id: store.getters.businessId,
  });

  if (isAuthenticatedUser.value) {
    router.push({ name: RouteNames.USER.INDEX });
    return;
  }
}
</script>

<template>
  <div :class="$style.wrapper">
    <div :class="$style.header">
      <div :class="$style.title">
        <div :class="$style.title__identification">
          <h3 :class="$style.title__identification__name">
            {{ props.owner.fullName }}
          </h3>
          <Tag
            v-if="isSignatory"
            :class="$style.title__identification__tag"
            value="Signatory"
          ></Tag>
        </div>
        <div
          v-if="!isEligibleToBeSignatory && isUserProfileCompleted"
          :class="$style.title__notification"
        >
          This individual does not meet the signatory criteria.
          <a
            href="https://help.clear.co/s/article/Adding-Additional-Users?language=en_US"
            target="_blank"
            >Learn more</a
          >
        </div>
      </div>

      <div :class="$style.actions">
        <button
          v-if="isAuthenticatedUser"
          :class="$style.actions__item"
          @click="editOwner()"
          :disabled="!canEditOwner"
          aria-label="pencil icon"
          v-tooltip.bottom.hover="editButtonTooltipText"
        >
          <span class="pi pi-pencil" :class="$style.actions__item__icon"></span>
        </button>
        <button
          :class="$style.actions__item"
          @click="$emit('removeOwner', props.owner.id)"
          :disabled="!canEditUser"
          aria-label="trash icon"
          v-tooltip.bottom.hover="trashIconTooltipText"
        >
          <span class="pi pi-trash" :class="$style.actions__item__icon"></span>
        </button>
      </div>
    </div>

    <div v-if="canSetAsSignatory" :class="$style.operations">
      <DSButton
        class="p-button-outlined"
        label="Set as signatory"
        @click="$emit('setAsSignatory')"
      />
    </div>

    <div :class="$style.details">
      <div :class="$style.details__row">
        <div :class="$style.details__row__left">Email</div>
        <div :class="$style.details__row__right">
          {{ props.owner.email }}
        </div>
      </div>
      <div v-if="isCompanyOwner" :class="$style.details__row">
        <div :class="$style.details__row__left">Ownership</div>
        <div :class="$style.details__row__right">25%+</div>
      </div>
      <div v-if="props.owner.jobTitle" :class="$style.details__row">
        <div :class="$style.details__row__left">Job Title</div>
        <div :class="$style.details__row__right">
          {{ props.owner.jobTitle }}
        </div>
      </div>
      <template v-if="isAuthenticatedUser">
        <div v-if="props.owner.personalTaxId" :class="$style.details__row">
          <div :class="$style.details__row__left">SSN Number</div>
          <div :class="$style.details__row__right">
            {{ props.owner.personalTaxId }}
          </div>
        </div>
        <div
          v-if="props.owner.isPrimary && ownerHasIdUploads(props.owner)"
          :class="$style.details__row"
        >
          <div :class="$style.details__row__left">ID</div>
          <div :class="$style.details__row__right">
            <button
              :class="$style['button-link']"
              @click="downloadFiles(props.owner.id)"
            >
              View ID
            </button>
          </div>
        </div>
        <div
          v-if="Object.keys(props.owner.address).length"
          :class="$style.details__row"
        >
          <div :class="$style.details__row__left">Home Address</div>
          <div
            :class="$style.details__row__right"
            v-html="formatAddress(props.owner.address)"
          ></div>
        </div>
        <div v-if="props.owner.phoneNumber" :class="$style.details__row">
          <div :class="$style.details__row__left">Phone Number</div>
          <div :class="$style.details__row__right">
            {{ props.owner.phoneNumber }}
          </div>
        </div>
        <div v-if="props.owner.citizenship" :class="$style.details__row">
          <div :class="$style.details__row__left">Citizenship</div>
          <div :class="$style.details__row__right">
            {{ getCountryName(props.owner.citizenship) }}
          </div>
        </div>
      </template>
      <div v-if="isDirectorBoardMember" :class="$style.details__row">
        <div :class="$style.details__row__left">Owner Type</div>
        <div :class="$style.details__row__right">Director/Board member</div>
      </div>
    </div>

    <div :class="$style.alerts">
      <Message
        v-if="hasPendingInvite"
        :class="$style.alerts__item"
        severity="warn"
        :closable="false"
      >
        <h3>ID verification pending</h3>
        <p>
          ID verification email sent. We're waiting for it to be completed. The
          profile will be updated once their identity is verified.
        </p>
        <p>
          <DSButton
            v-if="canResendInvite"
            class="p-button-link"
            :class="$style['button-link']"
            label="Resend email"
            :disabled="loadingUserInvite"
            :loading="loadingUserInvite"
            @click="resendInvite()"
          />
        </p>
      </Message>
      <Message
        v-if="!hasPendingInvite && !isUserProfileCompleted"
        :class="$style.alerts__item"
        severity="warn"
        :closable="false"
      >
        <template
          v-if="
            !userHasRequiredFieldsForVerification(props.owner) ||
            areDocumentUploadsRequired
          "
        >
          <h3>Incomplete Profile</h3>
          <p v-if="isAuthenticatedUser">
            Please complete this profile, so we can verify your identity.
          </p>
          <p v-else>
            Please ask this user to complete their profile, so we can verify
            their identity.
          </p>
          <p>
            <button
              v-if="canEditOwner"
              :class="$style['button-link']"
              @click="editOwner()"
            >
              Edit profile
            </button>
          </p>
        </template>
        <template v-else>
          <h3>Verification Pending</h3>
          <p>
            This profile is ready for identity verification. Please click Verify
            below to start the process.
          </p>
        </template>
      </Message>
    </div>
  </div>
</template>

<style scoped module lang="less">
.wrapper {
  padding: 20px;
  border: 1px solid var(--neutral-grey-100, #e8e8ea);
  background: var(--neutral-white, #fff);
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.06);
}

.wrapper:first-child {
  margin-top: 40px;
}

.wrapper + .wrapper {
  margin-top: 24px;
}

@media (min-width: 768px) {
  .wrapper {
    padding: 40px;
  }
}

/* ------ */
/* Header */
/* ------ */

.header {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
}

.title {
  margin-top: 20px;
}

.title__identification {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.title__identification__name {
  color: var(--neutral-black, #000);
  font-family: @regular-font;
  font-size: 20px;
  font-style: normal;
  font-weight: 500;
  line-height: 28px;
  margin: 0;
}

.title__identification__tag {
  font-size: 14px;
  font-family: @regular-font;
  background: var(--status-purple-light, #f3dfff);
  color: var(--status-purple-dark, #820ec6);
  margin-top: 10px;
}

.title__notification {
  margin-top: 4px;
  color: var(--neutral-black, #000);
  font-family: @regular-font;
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: 24px;
}

.title__notification a {
  display: inline-block;
  text-decoration: underline;
}

@media (min-width: 768px) {
  .header {
    flex-direction: row;
  }

  .title {
    margin: 0 20px 0 0;
  }

  .title__identification {
    flex-direction: row;
  }

  .title__identification__tag {
    margin: 0 0 0 8px;
  }
}

/* ------- */
/* Actions */
/* ------- */

.actions {
  display: flex;
  order: -1;
}

.actions__item {
  all: unset;
  padding: 4px;
  cursor: pointer;
}

.actions__item + .actions__item {
  margin-left: 20px;
}

.actions__item__icon {
  display: block;
  font-size: 20px;
}

.actions__item:disabled .actions__item__icon {
  color: rgba(0, 0, 0, 0.4);
}

.actions__item:hover .actions__item__icon {
  color: var(--color-cc-purple-500, #6f2dff);
}

.actions__item:disabled:hover .actions__item__icon {
  color: rgba(0, 0, 0, 0.4);
}

@media (min-width: 768px) {
  .actions {
    margin-left: auto;
    order: 0;
  }

  .actions__item + .actions__item {
    margin-left: 16px;
  }

  .actions__item__icon {
    font-size: auto;
  }
}

/* ---------- */
/* Operations */
/* ---------- */

.operations {
  margin-top: 40px;
}

/* ------- */
/* Details */
/* ------- */

.details {
  margin-top: 40px;
}

.details__row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.details__row + .details__row {
  margin-top: 16px;
  border-top: 1px dotted rgba(0, 0, 0, 0.1);
  padding-top: 16px;
}

.details__row__right {
  color: var(--neutral-black, #000);
  font-family: @regular-font;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  word-wrap: break-word;
  max-width: 100%;
}

.details__row__right a {
  font-weight: 400;
  text-decoration: underline;
}

@media (min-width: 768px) {
  .details__row {
    flex-direction: row;
    justify-content: space-between;
  }

  .details__row + .details__row {
    border-top: 0;
    padding-top: 0;
  }

  .details__row__right {
    margin-left: 40px;
    max-width: 60%;
    text-align: right;
  }
}

/* ------ */
/* Alerts */
/* ------ */

.alerts [class^='p-message'] {
  margin: 0;
}

.alerts__item:first-child {
  margin-top: 40px;
}

.alerts__item + .alerts__item {
  margin-top: 16px;
}

/* ------------ */
/* Misc Helpers */
/* ------------ */

.button-link {
  all: unset;
  color: var(--info-info-400, #215cb6);
  font-family: @regular-font;
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: 24px;
  text-decoration-line: underline;
  cursor: pointer;
}
</style>
